import React, { useState } from 'react';
import { format } from 'date-fns';

// Replicates the types from SessionDetail.tsx for the playbook usage data
interface SalesPlaybookQuestionAsked {
  questionId: string;
  questionText: string;
  tags: string[];
  askedAt: string;
}

interface SalesPlaybookQuestionNote {
  questionId: string;
  questionText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookPitchUsed {
  pitchId: string;
  pitchText: string;
  usedAt: string;
}

interface SalesPlaybookPitchNote {
  pitchId: string;
  pitchText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookAdvancedUsed {
  advancedId: string;
  advancedText: string;
  usedAt: string;
}

interface SalesPlaybookAdvancedNote {
  advancedId: string;
  advancedText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookRoleUsed {
  industry: string;
  industryDisplayName: string;
  role: string;
  roleDisplayName: string;
  questionsAsked: SalesPlaybookQuestionAsked[];
  questionNotes: SalesPlaybookQuestionNote[];
  pitchesUsed: SalesPlaybookPitchUsed[];
  pitchNotes: SalesPlaybookPitchNote[];
  advancedUsed: SalesPlaybookAdvancedUsed[];
  advancedNotes: SalesPlaybookAdvancedNote[];
}

export interface SalesPlaybookUsage {
  selectedIndustry?: string;
  selectedIndustryDisplayName?: string;
  selectedRole?: string;
  selectedRoleDisplayName?: string;
  totalQuestionsAvailable?: number;
  rolesUsed?: SalesPlaybookRoleUsed[];
}

interface PlaybookUsagePanelProps {
  salesPlaybookUsage?: SalesPlaybookUsage;
}

const TAG_COLORS: Record<string, string> = {
  situation: 'bg-blue-100 text-blue-700',
  pain: 'bg-red-100 text-red-700',
  cost_math: 'bg-green-100 text-green-700',
  authority: 'bg-purple-100 text-purple-700',
  timeline: 'bg-yellow-100 text-yellow-700',
  close_trigger: 'bg-orange-100 text-orange-700',
};

const TAG_LABELS: Record<string, string> = {
  situation: 'Situation',
  pain: 'Pain',
  cost_math: 'Cost Math',
  authority: 'Authority',
  timeline: 'Timeline',
  close_trigger: 'Close Trigger',
};

const PlaybookUsagePanel: React.FC<PlaybookUsagePanelProps> = ({ salesPlaybookUsage }) => {
  const [selectedPlaybookIndex, setSelectedPlaybookIndex] = useState(0);
  const [isPitchSectionCollapsed, setIsPitchSectionCollapsed] = useState(true);
  const [isQuestionsSectionCollapsed, setIsQuestionsSectionCollapsed] = useState(true);
  const [isAdvancedSectionCollapsed, setIsAdvancedSectionCollapsed] = useState(true);

  const allRolesUsed = salesPlaybookUsage?.rolesUsed ?? [];
  const rolesUsed = allRolesUsed.filter(
    (r) =>
      (r.pitchesUsed?.length ?? 0) > 0 ||
      (r.questionsAsked?.length ?? 0) > 0 ||
      (r.advancedUsed?.length ?? 0) > 0
  );

  if (!salesPlaybookUsage || rolesUsed.length === 0) {
    const playbookWasSelected = !!salesPlaybookUsage?.selectedIndustry;
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
        <div className="text-lg font-medium mb-1">
          {playbookWasSelected ? 'No Items Explicitly Tracked' : 'No Sales Playbook Used'}
        </div>
        <div className="text-sm">
          {playbookWasSelected
            ? 'The rep opened the playbook but did not mark any items as used. See the AI Coverage Analysis below for what was detected in the transcripts.'
            : 'This session did not use the Sales Playbook feature.'}
        </div>
      </div>
    );
  }

  const uniqueIndustries = new Set(rolesUsed.map((r) => r.industry));
  const totalPitches = rolesUsed.reduce((sum, r) => sum + (r.pitchesUsed?.length ?? 0), 0);
  const totalQuestions = rolesUsed.reduce((sum, r) => sum + (r.questionsAsked?.length ?? 0), 0);
  const totalAdvanced = rolesUsed.reduce((sum, r) => sum + (r.advancedUsed?.length ?? 0), 0);
  const totalNotes = rolesUsed.reduce(
    (sum, r) =>
      sum + (r.questionNotes?.length ?? 0) + (r.pitchNotes?.length ?? 0) + (r.advancedNotes?.length ?? 0),
    0
  );

  const selectedIndex = Math.min(selectedPlaybookIndex, rolesUsed.length - 1);
  const selectedRole = rolesUsed[selectedIndex];

  const questionNotesMap: Record<string, string> = {};
  selectedRole?.questionNotes?.forEach((n) => {
    questionNotesMap[n.questionId] = n.note;
  });
  const pitchNotesMap: Record<string, string> = {};
  selectedRole?.pitchNotes?.forEach((n) => {
    pitchNotesMap[n.pitchId] = n.note;
  });
  const advancedNotesMap: Record<string, string> = {};
  selectedRole?.advancedNotes?.forEach((n) => {
    advancedNotesMap[n.advancedId] = n.note;
  });

  return (
    <div className="space-y-3">
      {/* Overall Summary */}
      <div className="bg-white rounded-lg p-3 border border-gray-200">
        <h3 className="text-base font-semibold mb-2">Actual Playbook Usage</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <div className="text-xs text-gray-500">Industries</div>
            <div className="text-xl font-bold text-blue-600">{uniqueIndustries.size}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Roles Used</div>
            <div className="text-xl font-bold text-purple-600">{rolesUsed.length}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Pitches Used</div>
            <div className="text-xl font-bold text-indigo-600">{totalPitches}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Questions Asked</div>
            <div className="text-xl font-bold text-green-600">{totalQuestions}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Deal Progression</div>
            <div className="text-xl font-bold text-cyan-600">{totalAdvanced}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Total Notes</div>
            <div className="text-xl font-bold text-yellow-600">{totalNotes}</div>
          </div>
        </div>
      </div>

      {/* Role selection tabs */}
      <div className="bg-white rounded-lg p-3 border border-gray-200">
        <h3 className="text-xs font-semibold text-gray-700 mb-2">Industry / Role Combination</h3>
        <div className="flex flex-wrap gap-2">
          {rolesUsed.map((roleData, idx) => {
            const isSelected = idx === selectedIndex;
            const isLastUsed =
              roleData.industry === salesPlaybookUsage?.selectedIndustry &&
              roleData.role === salesPlaybookUsage?.selectedRole;
            const pitchesCount = roleData.pitchesUsed?.length ?? 0;
            const questionsCount = roleData.questionsAsked?.length ?? 0;
            const advancedCount = roleData.advancedUsed?.length ?? 0;

            return (
              <button
                key={`role-tab-${idx}`}
                onClick={() => {
                  setSelectedPlaybookIndex(idx);
                  setIsPitchSectionCollapsed(true);
                  setIsQuestionsSectionCollapsed(true);
                  setIsAdvancedSectionCollapsed(true);
                }}
                type="button"
                className={`px-3 py-2 rounded-lg border text-left transition-all ${
                  isSelected
                    ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                    {roleData.industryDisplayName || roleData.industry}
                  </span>
                  <span className="text-gray-400">/</span>
                  <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                    {roleData.roleDisplayName || roleData.role}
                  </span>
                  {isLastUsed && (
                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Last Used</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {pitchesCount} pitch{pitchesCount !== 1 ? 'es' : ''}, {questionsCount} question
                  {questionsCount !== 1 ? 's' : ''}, {advancedCount} deal progression
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected role details */}
      {selectedRole && (
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-800">
              {selectedRole.industryDisplayName || selectedRole.industry} /{' '}
              {selectedRole.roleDisplayName || selectedRole.role}
            </h3>
          </div>

          {/* Intro Pitches */}
          {selectedRole.pitchesUsed && selectedRole.pitchesUsed.length > 0 && (
            <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setIsPitchSectionCollapsed(!isPitchSectionCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                type="button"
              >
                <h4 className="text-xs font-semibold text-gray-700">
                  Intro Pitches ({selectedRole.pitchesUsed.length})
                </h4>
                <span
                  className="text-gray-400 text-xs"
                  style={{
                    transform: isPitchSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.15s',
                  }}
                >
                  &#9660;
                </span>
              </button>
              {!isPitchSectionCollapsed && (
                <div className="p-2 space-y-1.5">
                  {selectedRole.pitchesUsed.map((pitch, idx) => {
                    const note = pitchNotesMap[pitch.pitchId];
                    return (
                      <div key={`pitch-${idx}`} className="bg-indigo-50 rounded p-2">
                        <div className="flex items-start gap-2">
                          <span className="text-indigo-600 text-sm">&#10003;</span>
                          <div className="flex-1">
                            <div className="text-sm text-gray-800">{pitch.pitchText}</div>
                            {note && (
                              <div className="mt-1 text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-1.5">
                                <span className="font-medium">Note:</span> {note}
                              </div>
                            )}
                            <div className="text-xs text-gray-400">
                              Used at {format(new Date(pitch.usedAt), 'HH:mm:ss')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Questions Asked */}
          {selectedRole.questionsAsked && selectedRole.questionsAsked.length > 0 && (
            <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setIsQuestionsSectionCollapsed(!isQuestionsSectionCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                type="button"
              >
                <h4 className="text-xs font-semibold text-gray-700">
                  Questions Asked ({selectedRole.questionsAsked.length})
                </h4>
                <span
                  className="text-gray-400 text-xs"
                  style={{
                    transform: isQuestionsSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.15s',
                  }}
                >
                  &#9660;
                </span>
              </button>
              {!isQuestionsSectionCollapsed && (
                <div className="p-2 space-y-1.5">
                  {selectedRole.questionsAsked.map((q, idx) => {
                    const note = questionNotesMap[q.questionId];
                    return (
                      <div key={`question-${idx}`} className="bg-green-50 rounded p-2">
                        <div className="flex items-start gap-2">
                          <span className="text-green-600 text-sm">&#10003;</span>
                          <div className="flex-1">
                            <div className="text-sm text-gray-800">{q.questionText}</div>
                            {q.tags && q.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {q.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className={`px-1.5 py-0.5 rounded text-xs ${TAG_COLORS[tag] ?? 'bg-gray-100 text-gray-700'}`}
                                  >
                                    {TAG_LABELS[tag] ?? tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            {note && (
                              <div className="mt-1 text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-1.5">
                                <span className="font-medium">Note:</span> {note}
                              </div>
                            )}
                            <div className="text-xs text-gray-400">
                              Asked at {format(new Date(q.askedAt), 'HH:mm:ss')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Deal Progression */}
          {selectedRole.advancedUsed && selectedRole.advancedUsed.length > 0 && (
            <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setIsAdvancedSectionCollapsed(!isAdvancedSectionCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                type="button"
              >
                <h4 className="text-xs font-semibold text-gray-700">
                  Deal Progression ({selectedRole.advancedUsed.length})
                </h4>
                <span
                  className="text-gray-400 text-xs"
                  style={{
                    transform: isAdvancedSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.15s',
                  }}
                >
                  &#9660;
                </span>
              </button>
              {!isAdvancedSectionCollapsed && (
                <div className="p-2 space-y-1.5">
                  {selectedRole.advancedUsed.map((advanced, idx) => {
                    const note = advancedNotesMap[advanced.advancedId];
                    return (
                      <div key={`advanced-${idx}`} className="bg-cyan-50 rounded p-2">
                        <div className="flex items-start gap-2">
                          <span className="text-cyan-600 text-sm">&#10003;</span>
                          <div className="flex-1">
                            <div className="text-sm text-gray-800">{advanced.advancedText}</div>
                            {note && (
                              <div className="mt-1 text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-1.5">
                                <span className="font-medium">Note:</span> {note}
                              </div>
                            )}
                            <div className="text-xs text-gray-400">
                              Used at {format(new Date(advanced.usedAt), 'HH:mm:ss')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!selectedRole.pitchesUsed?.length &&
            !selectedRole.questionsAsked?.length &&
            !selectedRole.advancedUsed?.length && (
              <div className="text-center text-gray-500 py-3">
                No items were used for this industry/role combination.
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default PlaybookUsagePanel;
